import { useEffect, useState } from 'react';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { DirectoryItemAdmin } from '~/modules/directory/type';
import type { PinItemType } from '~/modules/moodboards/type';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { CarouselPaginationPill } from '~/ui-rocco/pages/home-page/components/CarouselPaginationPill';
import { DetailScrollReveal } from './DetailScrollReveal';
import { useIslandAutoplay } from './magic-island';
import { detailCta } from './detail-cta';
import { DetailTileCard } from './DetailTileCard';

/**
 * Minimal tile shape shared across detail pages (products, projects,
 * professionals, articles). `subtitle` is the second line under the title;
 * product/project tiles can pass their `professional.name` instead.
 *
 * The optional fields opt a tile into the shared tile behaviours: `pinType`
 * (+ `libraryItemId`) adds the hover Save overlay; `adminData` (+
 * `professionalLink`) adds the BM right-click admin menu. Omit them for plain
 * tiles. See `DetailTileMedia` for the convergence/migration plan.
 */
export interface DetailTile {
    id: number;
    link: string;
    title: string;
    images?: { src?: string; alt?: string; multiplied?: boolean }[];
    subtitle?: string;
    /**
     * The item's professional. `name` doubles as the subtitle fallback; `id`
     * (+ `professionalLink`) additionally arms the action panel's Enquire for
     * product / project tiles.
     */
    professional?: { id?: number; name?: string };
    /** Enables the hover Save (FollowSelector) overlay. */
    pinType?: PinItemType;
    libraryItemId?: number;
    /** Enables the BM right-click admin menu (projects / products only). */
    adminData?: DirectoryItemAdmin;
    professionalLink?: string;
    /** Professional logo + its badge background — renders a corner logo badge
     *  over the media (network tiles), matching the /professionals directory. */
    logo?: string | null;
    logoBackground?: string | null;
}

const Pagination = ({
    label,
    theme,
}: {
    label: string;
    theme: 'light' | 'dark';
}) => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-8 md:pt-[3rem]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label={label}
                theme={theme}
            />
        </div>
    );
};

export interface TileCarouselProps {
    /** Parenthesised eyebrow, e.g. "(Our Products)". */
    eyebrow: string;
    /**
     * Accessible name for the carousel landmark. Falls back to the eyebrow
     * text — pass one whenever `eyebrow` is empty (hidden-header embeds) so
     * carousels stay distinguishable in the landmark list.
     */
    ariaLabel?: string;
    tiles: DetailTile[];
    ctaLabel?: string;
    ctaTo?: string;
    /** Hide the per-tile subtitle / professional name (e.g. the installers row). */
    hideSubtitle?: boolean;
    /** Drop the eyebrow/hairline header — used when embedded in an accordion row. */
    hideHeader?: boolean;
    /** `light` = ink on white (product). `dark` = light type on a dark section. */
    theme?: 'light' | 'dark';
    /** Fired with the tile id when a tile is clicked (e.g. click analytics). */
    onTileClick?: (tileId: number) => void;
    /** Extra classes for the section wrapper (e.g. a page-specific gutter). */
    className?: string;
    /** Extra classes per carousel item (e.g. page-specific tile basis). */
    itemClassName?: string;
    /** Extra classes for the scroll content (e.g. page-specific item gap). */
    contentClassName?: string;
    /**
     * Classes for the embla viewport — gutter padding here keeps the first /
     * last snaps aligned to the page gutter while the slides scroll full-bleed
     * (see CarouselContent). Defaults to the section's own mobile gutter;
     * `hideHeader` embeds pass the host gutter instead.
     */
    viewportClassName?: string;
    /** Extra classes for the eyebrow/hairline row (e.g. a narrower gutter
     *  margin to match a page-specific `viewportClassName`). */
    headerClassName?: string;
    /** Extra classes for each tile's media box (e.g. a page-specific aspect). */
    tileMediaClassName?: string;
    /** Extra classes for each tile's media img (e.g. `object-contain`). */
    tileImgClassName?: string;
    /** Extra classes for each tile's title/subtitle lines (e.g. `truncate`). */
    tileTextClassName?: string;
    /** Extra classes for each tile's corner logo badge (size / inset). */
    tileLogoClassName?: string;
    /** Each tile's rendered width as a `sizes` string (see `getImagesSizes`). */
    tileSizes?: string;
    /** Drop the pagination pill under the carousel. */
    hidePagination?: boolean;
    /**
     * Opt into the Magic Island pagination + auto-scroll treatment (matching the
     * hero gallery): the frosted island pill drives play/pause, dot-jump and a
     * dwell-timer auto-advance that stops on the last snap. Hides the in-page
     * pagination pill (the island owns it).
     */
    islandAutoplay?: boolean;
    /** Accessible label for the island pagination, e.g. "More from {pro}". */
    islandLabel?: string;
    /** Dwell per slide (ms) when `islandAutoplay`. Default 4000. */
    autoplayDelayMs?: number;
    /** Stacking priority for the island registration. Default 1. */
    islandPriority?: number;
    /**
     * Extra gate on the island claim (default true) — e.g. the product page
     * hands the island to its enquiry fallback past half-scroll.
     */
    islandClaimEnabled?: boolean;
    /**
     * Centered CTA pill rendered under the carousel — used to re-surface a "view
     * more" link when `islandAutoplay` repurposes the island for pagination.
     */
    cta?: { label: string; to: string };
    /**
     * Fraction of the carousel that must be in view before its scroll-reveal
     * fires. Default 0.1; raise it for short, small-tile carousels.
     */
    revealThreshold?: number;
    /**
     * Fade-and-rise the carousel in on first reveal (default). False renders
     * it in place from the first paint; the reveal observer (and the island
     * gate it drives) still fires on schedule.
     */
    revealAnimation?: boolean;
    /**
     * The shared "work by this entity" mobile treatment (more from a supplier /
     * professional, related projects): two ~46%-width tiles with a peek of the
     * third, a 12px gap and a 16px viewport inset so the row reads as a scrollable
     * set rather than a full-bleed hero. Mobile-only — desktop metrics are
     * unchanged, so callers keep their own `md:` basis / aspect overrides. Pairs
     * with a host `-mx-*` full-bleed wrapper that cancels the section gutter.
     */
    peek?: boolean;
}

/**
 * Shared related-tile carousel for the revamped detail pages — a light eyebrow +
 * optional "view more" link above a free-scrolling card carousel, matching the
 * homepage Recently-viewed Products treatment. Self-hides when there are no
 * tiles with imagery.
 */
export const TileCarousel = ({
    eyebrow,
    ariaLabel,
    tiles,
    ctaLabel,
    ctaTo,
    hideSubtitle = false,
    hideHeader = false,
    theme = 'light',
    onTileClick,
    className,
    itemClassName,
    contentClassName,
    tileMediaClassName,
    tileImgClassName,
    tileTextClassName,
    tileLogoClassName,
    tileSizes,
    hidePagination = false,
    islandAutoplay = false,
    islandLabel,
    autoplayDelayMs,
    islandPriority,
    islandClaimEnabled = true,
    revealAnimation = true,
    cta,
    revealThreshold,
    viewportClassName,
    headerClassName,
    peek = false,
}: TileCarouselProps) => {
    const visible = tiles.filter((tile) => tile.images?.[0]?.src);
    const isDark = theme === 'dark';

    // The shared work-by-entity peek metrics (mobile only). All `max-md:` so
    // they layer cleanly under the caller's own `md:` overrides, and `peek` owns
    // the mobile viewport inset (16px) rather than the section's default gutter.
    const peekItemClassName = peek ? 'max-md:basis-[46%]' : undefined;
    const peekContentClassName = peek ? 'max-md:gap-3' : undefined;

    // The island owns the embla api when autoplay is on. The hook is called
    // unconditionally (rules of hooks) but no-ops while `enabled` is false — the
    // api stays null, so its timer effects never fire.
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    // Gate the island on the scroll-reveal: the reveal threshold can sit above
    // the island's reading line (small-tile carousels reveal at 80%), so without
    // this the pagination could show and autoplay could advance while the tiles
    // are still faded out.
    const [revealed, setRevealed] = useState(false);
    const islandRef = useIslandAutoplay<HTMLElement>(emblaApi, {
        label: islandLabel ?? `${eyebrow} carousel`,
        enabled:
            islandClaimEnabled &&
            islandAutoplay &&
            visible.length > 1 &&
            revealed,
        autoplayDelayMs,
        priority: islandPriority,
    });

    // Embedded in an accordion row, embla often first measures a zero-width
    // viewport (the row is collapsed / mid open-animation) and lays the slides
    // out against it, leaving the first slide offset once the row opens. Re-init
    // whenever the viewport gains or changes a real width so the snaps recompute
    // against the true size; snap back to the start on the first real width so a
    // slide laid out against zero doesn't stay half-scrolled.
    useEffect(() => {
        const viewport = emblaApi?.rootNode();
        if (!viewport || typeof ResizeObserver === 'undefined') return;
        let lastWidth = viewport.clientWidth;
        const observer = new ResizeObserver(() => {
            const width = viewport.clientWidth;
            if (width === lastWidth) return;
            const cameFromZero = lastWidth === 0;
            lastWidth = width;
            emblaApi?.reInit();
            if (cameFromZero && width > 0) emblaApi?.scrollTo(0, true);
        });
        observer.observe(viewport);
        return () => observer.disconnect();
    }, [emblaApi]);

    if (visible.length === 0) return null;

    return (
        <section
            ref={islandAutoplay ? islandRef : undefined}
            className={cn(
                'w-full overflow-hidden',
                isDark
                    ? 'bg-transparent text-white'
                    : 'bg-white text-[#1f1f21]',
                // Mobile keeps the section full-bleed (the carousel scrolls
                // edge-to-edge); the header row and viewport carry the gutter.
                hideHeader ? '' : 'md:px-[3rem]',
                className,
            )}
        >
            {hideHeader ? null : (
                <div
                    className={cn(
                        'mx-6 flex items-baseline justify-between border-t pt-4 md:mx-0 md:pt-[1.062rem]',
                        isDark ? 'border-white/20' : 'border-black/20',
                        headerClassName,
                    )}
                >
                    <SectionLabel
                        className={cn(
                            // 22px divider eyebrow (1.375rem @1920), no tracking;
                            // mobile snaps to the shared 14px eyebrow token.
                            'tracking-normal max-md:text-[0.875rem] md:text-[1.375rem]',
                            isDark && 'text-white',
                        )}
                    >
                        {eyebrow}
                    </SectionLabel>
                    {ctaLabel && ctaTo ? (
                        <RoccoLink
                            to={ctaTo}
                            className={cn(
                                'font-sans underline max-md:text-sm md:text-[1.248rem]',
                                isDark ? 'text-white' : 'text-[#1f1f21]',
                            )}
                        >
                            {ctaLabel}
                        </RoccoLink>
                    ) : null}
                </div>
            )}

            <DetailScrollReveal
                delay={120}
                threshold={revealThreshold}
                animate={revealAnimation}
                onReveal={islandAutoplay ? () => setRevealed(true) : undefined}
                className={hideHeader ? '' : 'max-md:pt-6 md:pt-[3rem]'}
            >
                <Carousel
                    setApi={setEmblaApi}
                    ariaLabel={
                        ariaLabel ?? (eyebrow.replace(/[()]/g, '') || undefined)
                    }
                    options={{
                        align: 'start',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        slidesToScroll: 'auto',
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    <CarouselContent
                        className={cn(
                            // Both breakpoints lay the row out on the tile
                            // grid's gutter — 8px on mobile, 24px from md — so
                            // a carousel row and a grid row put their cards in
                            // the same places. Desktop's gutter lives as slide
                            // padding (below) rather than a track gap so a
                            // partly-scrolled row keeps it too, and the negative
                            // margin pulls the first and last cards back flush
                            // with the grid's edges; mobile scrolls freely and
                            // just takes the gap.
                            'max-md:gap-2 md:-mx-[0.75rem]',
                            peekContentClassName,
                            contentClassName,
                        )}
                        viewportClassName={cn(
                            peek
                                ? 'max-md:px-4'
                                : viewportClassName || hideHeader
                                  ? undefined
                                  : 'px-6 md:px-0',
                            viewportClassName,
                        )}
                    >
                        {visible.map((tile) => (
                            <CarouselItem
                                key={tile.id}
                                className={cn(
                                    'max-md:basis-[60%] md:basis-1/4 md:px-[0.75rem]',
                                    peekItemClassName,
                                    itemClassName,
                                )}
                            >
                                <DetailTileCard
                                    tile={tile}
                                    theme={theme}
                                    hideSubtitle={hideSubtitle}
                                    onTileClick={onTileClick}
                                    mediaClassName={tileMediaClassName}
                                    imgClassName={tileImgClassName}
                                    textClassName={tileTextClassName}
                                    logoClassName={tileLogoClassName}
                                    sizes={tileSizes}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    {hidePagination || islandAutoplay ? null : (
                        <Pagination
                            label={`${eyebrow} carousel`}
                            theme={theme}
                        />
                    )}
                </Carousel>
                {cta ? (
                    <div className="flex justify-center max-md:pt-8 md:pt-[3rem]">
                        <RoccoLink
                            to={cta.to}
                            className={detailCta(
                                isDark ? 'filled-dark' : 'filled-light',
                            )}
                        >
                            {cta.label}
                        </RoccoLink>
                    </div>
                ) : null}
            </DetailScrollReveal>
        </section>
    );
};
